import type { Geofence } from '@gpx-spa/geo-domain'
import type { ShipmentRouteBatchRow } from '@gpx-spa/queries/src/getAccountShipmentRoutesBatch'
import type { V2ShipmentStop } from '@gpx-spa/queries/src/getAccountShipmentV2'
import { computeStopsRouteFingerprint } from '@/utils/compute-stops-route-fingerprint'
import { v2ShipmentStopsToFormStopRows } from '@/components/shipment/form/utils/shipment-form-hydrate-stops'
import { shipmentRouteBatchRowsToRoutePlan } from '@/utils/shipment-route-batch-to-route-plan'
import { suggestedGeometryQualityDetailFromPaths } from '@/components/shipment/form/domain/suggested-geometry-quality'
import {
  COMPOSER_CORRIDOR_BUFFER_DEFAULT_M,
} from '@/components/shipment/form/domain/constants'
import {
  defaultSelectedByPathId,
} from '@/components/shipment/form/utils/shipment-form-route-composer-snapshot'
import {
  isPlannedRangeComplete,
} from '@/components/shipment/form/utils/shipment-form-planned-range-sync'
import type {
  ShipmentFormStopRow,
  useShipmentFormStore,
} from '@/stores/useShipmentFormStore'

export function hydrateFormStoreFromShipment(
  store: ReturnType<typeof useShipmentFormStore>,
  row: {
    id: number
    name?: string
    carrier?: string | null
    notes?: string | null
    asset_value?: number | null
    stops?: unknown[] | null
  },
  options?: {
    geofences?: readonly Geofence[]
    routeBatchRows?: readonly ShipmentRouteBatchRow[]
  },
) {
  store.shipmentId = row.id
  store.generalName = row.name ?? ''
  store.generalCarrier = row.carrier ?? ''
  store.generalNotes = row.notes ?? ''
  store.generalAssetValueCents = row.asset_value ?? null
  store.markMetadataPersisted(
    store.generalName,
    store.generalCarrier,
    store.generalNotes,
    store.generalAssetValueCents,
  )
  const raw = row.stops
  const apiStops = Array.isArray(raw) ? (raw as V2ShipmentStop[]) : []
  const stopCount = apiStops.length
  if (stopCount > 0) {
    store.geoCache.clear()
    const mapped = v2ShipmentStopsToFormStopRows(
      apiStops,
      options?.geofences ?? [],
    ) as ShipmentFormStopRow[]

    const batch = options?.routeBatchRows ?? []
    const routeRows = batch.some((r) => r.shipment_id === row.id)
      ? batch.filter((r) => r.shipment_id === row.id)
      : batch

    store.serverRoutesEmptyAtHydrate = routeRows.length === 0

    const plan = shipmentRouteBatchRowsToRoutePlan(routeRows, apiStops)
    const hasPaths = Boolean(
      plan?.segments?.some((s) => Array.isArray(s.paths) && s.paths.length > 0),
    )

    if (plan && hasPaths) {
      store.routePlan = plan
      let maxCw = 0
      for (const r of routeRows) {
        const w = r.corridor_width_meters
        if (typeof w === 'number' && Number.isFinite(w) && w > maxCw) maxCw = w
      }
      store.corridorBufferM =
        maxCw > 0 ? maxCw : COMPOSER_CORRIDOR_BUFFER_DEFAULT_M

      const allPaths = plan.segments.flatMap((s) => s.paths ?? [])
      store.geometryQualityPercent =
        suggestedGeometryQualityDetailFromPaths(allPaths).percent
      store.serverHydratedRouteFingerprint =
        computeStopsRouteFingerprint(mapped)
      store.markRouteComposerPersisted(
        store.corridorBufferM,
        store.geometryQualityPercent,
        defaultSelectedByPathId(plan),
      )
    } else {
      store.routePlan = null
      store.corridorBufferM = COMPOSER_CORRIDOR_BUFFER_DEFAULT_M
      store.geometryQualityPercent = 100
      // Baseline stop coords from server — skip Google until the user edits a stop.
      store.serverHydratedRouteFingerprint =
        computeStopsRouteFingerprint(mapped)
    }

    store.stops = mapped
    store.stopsSyncedOnDeviceApi = true
    store.markStopsPayloadPersisted(mapped)
  } else {
    store.serverHydratedRouteFingerprint = null
    store.stopsSyncedOnDeviceApi = false
    store.serverRoutesEmptyAtHydrate = true
  }
  store.syncGeneralPlannedRangeFromStops()
  if (isPlannedRangeComplete(store.generalPlannedRange)) {
    store.markAnchorPlannedSlotsManual()
  }
}
